import {arrayFrom, convertToHtmlTag, processingHtmlBrackets} from "../utils";
import isEmptyObject from "is-empty-object"
import escapeHtml from "escape-html"
import unescapeHtml from "unescape-html"
import CodeMirror from 'codemirror';
import Convert from 'ansi-to-html';


const ACCESS_CONTROL_EXCEPTION = "java.security.AccessControlException";
const SECURITY_MESSAGE = "Access control exception due to security reasons in web playground";
const UNHANDLED_JS_EXCEPTION = "Unhandled JavaScript exception";
const NO_TEST_FOUND = "No tests methods are found";
const ANGLE_BRACKETS_LEFT_HTML = "&lt;";
const ANGLE_BRACKETS_RIGHT_HTML = "&gt;";

const TEST_STATUS = {
  FAIL : { value: "FAIL", text: "Fail" },
  ERROR: { value: "ERROR", text: "Error" },
  PASSED : { value: "OK", text: "Passed" }
};

const BUG_FLAG = `${ANGLE_BRACKETS_LEFT_HTML}errStream${ANGLE_BRACKETS_RIGHT_HTML}BUG${ANGLE_BRACKETS_LEFT_HTML}/errStream${ANGLE_BRACKETS_RIGHT_HTML}`;
const BUG_REPORT_MESSAGE = `${ANGLE_BRACKETS_LEFT_HTML}errStream${ANGLE_BRACKETS_RIGHT_HTML}Hey! It seems you just found a bug! \uD83D\uDC1E\n` +
  `Please click <a href=http://kotl.in/issue target=_blank>here<a> to submit it ` +
  `to the issue tracker and one day we fix it, hopefully \uD83D\uDE09\n` +
  `✅ Don't forget to attach code to the issue${ANGLE_BRACKETS_LEFT_HTML}/errStream${ANGLE_BRACKETS_RIGHT_HTML}\n`;

/**
 * Process JVM output without SQL support.
 * @param {string} output
 * @param {string} theme
 * @returns {string}
 */
export function processJVMOutput(output, theme) {
  let processedOutput = processingHtmlBrackets(output); // don't need to escape `&`
  const processedOutputTrimmed = processedOutput.trim();
  return {
    standardOutputRaw: processedOutputTrimmed,
    standardOutput: processedOutput
      .split(BUG_FLAG).join(BUG_REPORT_MESSAGE)
      .split(`${ANGLE_BRACKETS_LEFT_HTML}outStream${ANGLE_BRACKETS_RIGHT_HTML}`).join(`<span class="standard-output ${theme}">`)
      .split(`${ANGLE_BRACKETS_LEFT_HTML}/outStream${ANGLE_BRACKETS_RIGHT_HTML}`).join("</span>")
      .split(`${ANGLE_BRACKETS_LEFT_HTML}errStream${ANGLE_BRACKETS_RIGHT_HTML}`).join(`<span class="error-output ${theme}">`)
      .split(`${ANGLE_BRACKETS_LEFT_HTML}/errStream${ANGLE_BRACKETS_RIGHT_HTML}`).join("</span>"),
  };
}

const OUTPUT_OBJECT_MARKER = "===== OutputObject =====";

/**
 * Joins SELECT fields with newlines when the table prefix changes.
 * Fields with the same table prefix are joined on the same line with ", ".
 * When the table prefix changes, a newline + indent is inserted.
 * 
 * This function is isolated so it can be easily replaced with `fields.join(', ')` if desired.
 * 
 * @param {string[]} fields - Array of field strings in "table.column" format
 * @param {string} indent - The indentation string to use after newlines
 * @returns {string} - The joined fields with appropriate newlines
 */
export function joinSelectFieldsWithTableGrouping(fields, indent) {
  if (!fields || fields.length === 0) {
    return '';
  }
  
  if (fields.length === 1) {
    return fields[0];
  }
  
  let result = fields[0];
  let currentPrefix = fields[0].split('.')[0];
  
  for (let i = 1; i < fields.length; i++) {
    const field = fields[i];
    const prefix = field.split('.')[0];
    
    if (prefix !== currentPrefix) {
      // Table prefix changed - add newline before this field
      result += ',\n' + indent + field;
      currentPrefix = prefix;
    } else {
      // Same table prefix - continue on same line
      result += ', ' + field;
    }
  }
  
  return result;
}

/**
 * Reformats SQL SELECT clauses by consolidating simple field references onto fewer lines.
 * Groups fields by their table prefix, putting fields with the same prefix on the same line.
 * Only processes queries where the SELECT clause contains ONLY simple <table>.<column> patterns.
 * If anything else is detected (functions, expressions, aliases, *, etc.) or parsing fails,
 * returns the original query unchanged.
 * 
 * @param {string} sqlQuery - The SQL query string to potentially reformat
 * @returns {string} - The reformatted query, or the original if reformatting is not applicable
 */
export function consolidateSelectFields(sqlQuery) {
  // Return original if input is invalid
  if (!sqlQuery || typeof sqlQuery !== 'string') {
    return sqlQuery;
  }

  try {
    // Find SELECT keyword (case-insensitive)
    const selectRegex = /\bSELECT\b/gi;
    const selectMatch = selectRegex.exec(sqlQuery);
    
    if (!selectMatch) {
      return sqlQuery;
    }
    
    const selectIndex = selectMatch.index;
    const selectEndIndex = selectIndex + 6; // "SELECT" is 6 chars
    
    // Find FROM after SELECT (case-insensitive)
    const afterSelect = sqlQuery.substring(selectEndIndex);
    const fromRegex = /\bFROM\b/gi;
    const fromMatch = fromRegex.exec(afterSelect);
    
    if (!fromMatch) {
      return sqlQuery;
    }
    
    const fromIndexRelative = fromMatch.index;
    const fromIndexAbsolute = selectEndIndex + fromIndexRelative;
    
    // Extract the content between SELECT and FROM
    const selectContent = sqlQuery.substring(selectEndIndex, fromIndexAbsolute);
    
    // Check if fields are spread across multiple lines (otherwise no reformatting needed)
    if (!selectContent.includes('\n')) {
      return sqlQuery;
    }
    
    // Split by comma and clean up each field
    const rawFields = selectContent.split(',');
    const fields = rawFields.map(f => f.trim()).filter(f => f.length > 0);
    
    if (fields.length === 0) {
      return sqlQuery;
    }
    
    // Check if ALL fields match the simple pattern: identifier.identifier
    // identifier = starts with letter or underscore, followed by alphanumeric or underscore
    const simpleFieldPattern = /^[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_]*$/;
    
    for (const field of fields) {
      if (!simpleFieldPattern.test(field)) {
        // Field doesn't match simple pattern, return unchanged
        return sqlQuery;
      }
    }
    
    // All fields are simple - find the indentation used
    let indent = '  '; // default
    const indentMatch = selectContent.match(/\n(\s+)/);
    if (indentMatch) {
      indent = indentMatch[1];
    }
    
    // Consolidate fields with table grouping (can be replaced with `fields.join(', ')` if desired)
    const consolidatedFields = joinSelectFieldsWithTableGrouping(fields, indent);
    
    // Reconstruct the query
    const beforeSelect = sqlQuery.substring(0, selectIndex);
    const selectKeyword = sqlQuery.substring(selectIndex, selectEndIndex);
    const fromAndRest = sqlQuery.substring(fromIndexAbsolute);
    
    return beforeSelect + selectKeyword + '\n' + indent + consolidatedFields + '\n' + fromAndRest;
  } catch (e) {
    // Any error in parsing - return original unchanged
    return sqlQuery;
  }
}

/**
 * Apply SQL syntax highlighting to plain SQL text
 * @param sqlText - plain SQL text (may contain HTML entities)
 * @returns {string} - HTML with syntax highlighting spans
 */
export function applySqlSyntaxHighlighting(sqlText) {
  if (!sqlText || typeof sqlText !== 'string') {
    return '';
  }

  // Decode HTML entities first (e.g., &gt; to >, &lt; to <)
  const decodedSql = unescapeHtml(sqlText);

  let highlightedHtml = '';

  // Use CodeMirror.runMode to parse and highlight the SQL
  CodeMirror.runMode(decodedSql, 'text/x-pgsql', (text, style) => {
    if (style) {
      const className = 'cm-' + style.replace(/ +/g, ' cm-');
      highlightedHtml += `<span class="${className}">${escapeHtml(text)}</span>`;
    } else {
      highlightedHtml += escapeHtml(text);
    }
  });

  // Wrap the highlighted SQL with a query separator container
  return `<div class="sql-query-separator">${highlightedHtml}</div>`;
}

/**
 * Process SQL output initial content by splitting on "---" separator lines
 * and wrapping each part in sql-query-separator divs.
 * @param sqlText - raw SQL text from textarea
 * @returns {string} - HTML with each SQL query wrapped separately
 */
export function processSqlOutputInitial(sqlText) {
  if (!sqlText || typeof sqlText !== 'string') {
    return '';
  }

  // Split by lines that contain only "===" (with optional whitespace)
  const parts = sqlText.split(/\n===\n/);
  
  // Apply SELECT field consolidation and SQL syntax highlighting to each part
  return parts
    .map(part => part.trim())
    .filter(part => part.length > 0)
    .map(part => applySqlSyntaxHighlighting(consolidateSelectFields(part)))
    .join('\n');
}

/**
 * Process standard output initial content by splitting on "---" separator lines
 * and wrapping each part in output-text-separator divs.
 * @param outputText - raw output text from textarea
 * @returns {string} - HTML with each output part wrapped separately
 */
export function processOutputInitial(outputText) {
  if (!outputText || typeof outputText !== 'string') {
    return '';
  }

  // Split by lines that contain only "===" (with optional whitespace)
  const parts = outputText.split(/\n===\n/);
  
  // Wrap each part in output-text-separator divs
  return parts
    .map(part => part.trim())
    .filter(part => part.length > 0)
    .map(part => `<div class="output-text-separator">${part}</div>`)
    .join('\n');
}

/**
 * Parse the output to extract SQL and standard output if the OutputObject marker is present.
 * @param rawOutput: string - raw output text from backend
 * @returns {sql: string | null, output: string}
 */
export function parseOutputObject(rawOutput) {
  try {
    // Parse the JSON after the marker
    const parsed = JSON.parse(rawOutput);
    return {
      sql: parsed.sql || null,
      output: parsed.output || ''
    };
  } catch (e) {
    // If parsing fails, return the whole output as standard output
    console.error('Failed to parse OutputObject JSON:', e);
    return { sql: null, output: rawOutput };
  }
}

/**
   * Put everything between startTag and endTag into sinkInsideTags and everything outside of them into sinkOutsideTags
   * @param {string} stringToProcess
   * @param {string} startTag 
   * @param {string} endTag 
   * @param {function(string): void} sinkInsideTags 
   * @param {function(string): void} sinkOutsideTags 
   */
function processStreamParts(stringToProcess, startTag, endTag, sinkInsideTags, sinkOutsideTags) { //sink: (output: string) => void
  let currentPos = 0;
  let startTagPos = stringToProcess.indexOf(startTag, currentPos);
  
  while (startTagPos !== -1) {
    // Everything before the start tag is outside content
    if (startTagPos > currentPos) {
      const outsideContent = stringToProcess.substring(currentPos, startTagPos);
      if (sinkOutsideTags) {
        sinkOutsideTags(outsideContent);
      }
    }
    
    // Find the corresponding end tag
    const contentStart = startTagPos + startTag.length;
    const endTagPos = stringToProcess.indexOf(endTag, contentStart);
    
    if (endTagPos === -1) {
      // No matching end tag found, treat rest as outside content
      const remainingContent = stringToProcess.substring(startTagPos);
      if (sinkOutsideTags) {
        sinkOutsideTags(remainingContent);
      }
      return;
    }
    
    // Extract content between tags
    const insideContent = stringToProcess.substring(contentStart, endTagPos);
    if (sinkInsideTags) {
      sinkInsideTags(insideContent);
    }
    
    // Move past the end tag
    currentPos = endTagPos + endTag.length;
    startTagPos = stringToProcess.indexOf(startTag, currentPos);
  }
  
  // Any remaining content after the last tag is outside content
  if (currentPos < stringToProcess.length) {
    const remainingContent = stringToProcess.substring(currentPos);
    if (sinkOutsideTags) {
      sinkOutsideTags(remainingContent);
    }
  }
}

/**
 * Process JVM output with SQL support.
 * Combines the functionality of processJVMOutput and parseOutputObject.
 * First extracts the output streams, then checks for SQL data.
 * @param output - raw output text from backend
 * @param theme - current theme
 * @returns {{standardOutput: string, sqlOutput: string | null}}
 */
export function processJVMOutputWithSql(output, theme) {
  // Step 1: Process HTML brackets first
  const htmlProcessedOutput = processingHtmlBrackets(output);

  // Step 2: Extract different output streams before parsing
  const processedOutput = htmlProcessedOutput.split(BUG_FLAG).join(BUG_REPORT_MESSAGE);

  // Extract standard output
  const outStreamStart = `${ANGLE_BRACKETS_LEFT_HTML}outStream${ANGLE_BRACKETS_RIGHT_HTML}`;
  const outStreamEnd = `${ANGLE_BRACKETS_LEFT_HTML}/outStream${ANGLE_BRACKETS_RIGHT_HTML}`;
  const errStreamStart = `${ANGLE_BRACKETS_LEFT_HTML}errStream${ANGLE_BRACKETS_RIGHT_HTML}`;
  const errStreamEnd = `${ANGLE_BRACKETS_LEFT_HTML}/errStream${ANGLE_BRACKETS_RIGHT_HTML}`;
  const objectStreamStart = `${ANGLE_BRACKETS_LEFT_HTML}outputObject${ANGLE_BRACKETS_RIGHT_HTML}`;
  const objectStreamEnd = `${ANGLE_BRACKETS_LEFT_HTML}/outputObject${ANGLE_BRACKETS_RIGHT_HTML}`;

  // Extract outputs by splitting on stream tags
  let standardOutput = '';
  let errorOutput = '';
  let bugOutput = '';
  /**
   * List of SQL queries found in the output
   * @type {string[]}
   */
  let sqlQueries = [];
  
  // Split by outStream tags to get standard output
  processStreamParts(processedOutput,outStreamStart, outStreamEnd, (output) => {
    // Within the <outputStream>...</outputStream> tags, look for <outputObject>...</outputObject> tags
    // and extract the content between them separately
    processStreamParts(output, objectStreamStart, objectStreamEnd, 
      (outputObjectString) => {
        const parsedOutputObject = parseOutputObject(outputObjectString);
        sqlQueries.push(parsedOutputObject.sql);
        if (parsedOutputObject.output.trim() !== '') {
          standardOutput += `<div class="output-text-separator">${parsedOutputObject.output.trim()}</div>`;
        }
      },
      (outputOutside) => {
        standardOutput += outputOutside.trim();
      }
    );
  });


  // Split by errStream tags to get error output
  processStreamParts(processedOutput, errStreamStart, errStreamEnd, (output) => errorOutput += output);


  // Step 4: Apply SELECT field consolidation and SQL syntax highlighting if we have SQL content
  let highlightedSqlContent =
    sqlQueries.length > 0 
      ? sqlQueries.map(sql => applySqlSyntaxHighlighting(consolidateSelectFields(sql))).join('\n') 
      : null;

  // Step 5: Reassemble the output with proper HTML spans
  let result = '';

  const convert = new Convert();

  // Not sure if this does anything now
  if (!(bugOutput.trim() === '')) {
    const htmlBugOutput = convert.toHtml(bugOutput);
    result += `<span class="error-output ${theme}">${htmlBugOutput}</span>\n`;
  }

  if (!(errorOutput.trim() === '')) {
    const htmlErrorOutput = convert.toHtml(errorOutput);
    result += `<span class="error-output ${theme}">${htmlErrorOutput}</span>\n`;
  }

  const standardOutputTrimmed = standardOutput.trim();
  const htmlOutput = convert.toHtml(standardOutputTrimmed);
  result += `<span class="standard-output ${theme}">${htmlOutput}</span>`;

  // Always return consistent object shape
  return {
    standardOutputRaw: standardOutputTrimmed,
    standardOutput: result,
    sqlOutput: highlightedSqlContent
  };
}

export function processJUnitResults(data, onTestPassed, onTestFailed) {
  let result = "";
  let totalTime = 0;
  let passed = true;
  if (isEmptyObject(data)) return NO_TEST_FOUND;
  for (let testClass in data) {
    let listOfResults = arrayFrom(data[testClass]);
    result += listOfResults.reduce((previousTest, currentTest) => {
      totalTime = totalTime + (currentTest.executionTime / 1000);
      if (currentTest.status === TEST_STATUS.ERROR.value || currentTest.status === TEST_STATUS.FAIL.value) passed = false;
      switch (currentTest.status) {
        case TEST_STATUS.FAIL.value:
          return previousTest + buildOutputTestLine(TEST_STATUS.FAIL.text, currentTest.methodName, currentTest.comparisonFailure.message);
        case TEST_STATUS.ERROR.value:
          return previousTest + buildOutputTestLine(TEST_STATUS.ERROR.text, currentTest.methodName, currentTest.exception.message);
        case TEST_STATUS.PASSED.value:
          return previousTest + buildOutputTestLine(TEST_STATUS.PASSED.text, currentTest.methodName, "");
      }
    }, "");
  }
  if (passed && onTestPassed) onTestPassed();
  if (!passed && onTestFailed) onTestFailed();
  let testTime = `<div class="test-time">Total test time: ${totalTime}s</div>`;
  const resultTrimmed = result.trim();
  return {
    standardOutputRaw: resultTrimmed,
    standardOutput: testTime + resultTrimmed,
  };
}

function buildOutputTestLine(status, method, message) {
  return `
  <div class="console-block">
    <span class="console-icon ${status.toLocaleLowerCase()}"></span>
    <div class="test-${status.toLocaleLowerCase()}">${status}: ${method}${message ? ': ' + convertToHtmlTag(message) : ''}</div>
  </div>
  `;
}

/**
 * 
 * @param {*} errors 
 * @param {*} theme 
 * @returns {string}
 */
export function processErrors(errors, theme) {
  const convert = new Convert();
  // escape HTML elements coming from the backend but then afterward turn ASCII colors to HTML colors (via convert.toHtml)
  const errorAccum = errors
    .reduce((acc, currentValue) => {
        return acc + `<div class="console-block">
                        <span class="console-icon attention"></span>
                        <div class="test-fail ${theme}">${convert.toHtml(escapeHtml(currentValue.message))}</div>
                      </div>`
      }
      , "");
  return errorAccum
}

/**
 * Check the security exception in the exception tree.
 * @param exception - json object that describes an exception
 * @returns {Object} add default security message to exception object if security exception is found
 */
export function findSecurityException(exception) {
  let currentException = exception;
  while (currentException != null) {
    if (currentException.fullName === ACCESS_CONTROL_EXCEPTION) {
      return getSecurityException(currentException);
    }
    currentException = currentException.cause;
  }
  return exception;
}

export function getExceptionCauses(exception) {
  if (exception.cause !== undefined && exception.cause != null) {
    return [exception.cause].concat(getExceptionCauses(exception.cause))
  } else {
    return []
  }
}

export function showJsException(exception) {
  console && console.error(exception);

  if (exception.stack != null) {
    let userStackTrace = exception.stack.toString().substr(0, exception.stack.toString().indexOf("at eval (<anonymous>)"));
    return `${UNHANDLED_JS_EXCEPTION}: ${exception.message} \n ${userStackTrace}`;
  } else {
    return UNHANDLED_JS_EXCEPTION;
  }
}

/**
 * Override exception message: append default security message.
 * Cut stack trace array - use only last stack trace element
 * @param exception - json object that describes an exception
 * @returns {Object} exception with default security message
 */
function getSecurityException(exception) {
  if (exception.stackTrace != null) {
    if (exception.message != null) {
      exception.message = `${SECURITY_MESSAGE}: \n ` + exception.message;
    } else {
      exception.message = SECURITY_MESSAGE
    }
    exception.stackTrace = exception.stackTrace.slice(exception.stackTrace.length - 1)
  }
  return exception
}
